import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { NAVIGATION, NavItem, MegaMenuColumn } from '../constants';

// ============================================================
// MEGA MENU PANEL (dropdown content)
// ============================================================

const MegaMenuPanel: React.FC<{ columns: MegaMenuColumn[]; onClose: () => void }> = ({ columns, onClose }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className="absolute top-full left-1/2 -translate-x-1/2 w-screen max-w-5xl mt-2 z-50"
    >
      {/* Invisible bridge to prevent menu closing when moving mouse */}
      <div className="h-2 w-full" />
      
      <div className="bg-brand-black/98 backdrop-blur-xl border border-white/10 rounded-lg shadow-2xl overflow-hidden">
        {/* Decorative top line */}
        <div className="h-[2px] bg-gradient-to-r from-transparent via-brand-red to-transparent" />
        
        <div className="p-8">
          <div className="grid grid-cols-3 gap-8">
            {columns.map((column, colIdx) => (
              <div key={colIdx}>
                {/* Column header */}
                <h3 className="text-[10px] font-bold tracking-[0.25em] text-brand-red mb-4 uppercase">
                  {column.title}
                </h3>
                
                {/* Menu items */}
                <ul className="space-y-1">
                  {column.items.map((item, itemIdx) => (
                    <li key={itemIdx}>
                      <Link
                        to={item.href}
                        onClick={onClose}
                        className="group flex items-start gap-3 px-3 py-2.5 rounded-md hover:bg-white/5 transition-all duration-200"
                      >
                        {item.icon && (
                          <span className="text-brand-red/60 group-hover:text-brand-red transition-colors mt-0.5 shrink-0">
                            {item.icon}
                          </span>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-white/90 group-hover:text-white transition-colors flex items-center gap-2">
                            {item.name}
                            <ArrowRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-brand-red" />
                          </div>
                          {item.description && (
                            <p className="text-xs text-white/40 group-hover:text-white/60 mt-0.5 leading-relaxed transition-colors">
                              {item.description}
                            </p>
                          )}
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
                
                {/* Featured CTA (if exists) */}
                {column.featured && (
                  <div className="mt-6 p-4 bg-brand-red/5 border border-brand-red/20 rounded-lg">
                    <h4 className="text-sm font-bold text-white mb-1">{column.featured.title}</h4>
                    <p className="text-xs text-white/50 mb-3 leading-relaxed">{column.featured.description}</p>
                    <Link
                      to={column.featured.href}
                      onClick={onClose}
                      className="inline-flex items-center gap-2 text-xs font-bold text-brand-red hover:text-white transition-colors"
                    >
                      {column.featured.cta}
                      <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        
        {/* Bottom bar */}
        <div className="border-t border-white/5 px-8 py-3 flex items-center justify-between">
          <span className="text-[10px] text-white/30 tracking-wider uppercase">
            E3 Methodology — Embedded. Essential. Engineered.
          </span>
          <Link
            to="/contact"
            onClick={onClose}
            className="text-xs font-bold text-brand-red hover:text-white transition-colors flex items-center gap-2"
          >
            LET'S TALK
            <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

// ============================================================
// MEGA MENU COMPONENT
// ============================================================

const MegaMenu: React.FC = () => {
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();

  // Close menu on route change
  useEffect(() => {
    setActiveMenu(null);
  }, [location.pathname]);

  const handleMouseEnter = (name: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setActiveMenu(name);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setActiveMenu(null), 150);
  };

  const handleClick = (item: NavItem) => {
    if (item.type === 'mega') {
      setActiveMenu(activeMenu === item.name ? null : item.name);
    }
  };

  return (
    <nav className="hidden lg:flex items-center gap-1" onMouseLeave={handleMouseLeave}>
      {NAVIGATION.map((item) => (
        <div key={item.name} className="relative">
          {item.type === 'mega' ? (
            <button
              onMouseEnter={() => handleMouseEnter(item.name)}
              onClick={() => handleClick(item)}
              className={`flex items-center gap-1.5 px-4 py-2 text-xs font-bold tracking-[0.15em] transition-all duration-200 rounded-md ${
                activeMenu === item.name
                  ? 'text-brand-red bg-white/5'
                  : 'text-white/70 hover:text-white hover:bg-white/5'
              }`}
            >
              {item.name}
              <ChevronDown
                className={`w-3 h-3 transition-transform duration-200 ${
                  activeMenu === item.name ? 'rotate-180' : ''
                }`}
              />
            </button>
          ) : (
            <Link
              to={item.href}
              className="px-4 py-2 text-xs font-bold tracking-[0.15em] text-white/70 hover:text-white transition-colors rounded-md hover:bg-white/5"
            >
              {item.name}
            </Link>
          )}

          {/* Mega menu panel */}
          <AnimatePresence>
            {activeMenu === item.name && item.megaMenu && (
              <MegaMenuPanel
                columns={item.megaMenu}
                onClose={() => setActiveMenu(null)}
              />
            )}
          </AnimatePresence>
        </div>
      ))}
    </nav>
  );
};

export default MegaMenu;
